import React, { useState, useEffect, useCallback } from 'react';
import { 
  MapPinIcon, 
  ChartBarIcon, 
  FunnelIcon, 
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import GISMap from '../components/GISMap';
import { gisService, HeatmapPoint, ClusteredPoint, ServiceArea, GISFilters, GISStats } from '../services/gisService';
import { apiService } from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Button from '../components/ui/Button';

const GISAnalytics: React.FC = () => {
  // State management
  const [heatmapData, setHeatmapData] = useState<HeatmapPoint[]>([]);
  const [clusteredData, setClusteredData] = useState<ClusteredPoint[]>([]);
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [gisStats, setGisStats] = useState<GISStats | null>(null);
  const [technicians, setTechnicians] = useState<any[]>([]);
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'heatmap' | 'clusters' | 'areas' | 'analytics'>('overview');
  
  // Layer visibility
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showClusters, setShowClusters] = useState(true);
  const [showServiceAreas, setShowServiceAreas] = useState(true);
  const [showTechnicians, setShowTechnicians] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState<GISFilters>({
    radiusKm: 50,
    zoomLevel: 10,
  });
  
  const [departments, setDepartments] = useState<any[]>([]);

  // Deterministic trend projection (linear regression over real historical
  // monthly counts — not an AI guess) of which wards are likely to generate
  // the most reports next month. Explicitly refuses to project when there
  // isn't enough historical data, rather than showing a confident-looking
  // number built on too little.
  interface HotspotForecast {
    available: boolean;
    reason?: string;
    monthsAnalyzed?: number;
    wards?: Array<{ wardNumber: number; monthsOfData: number; totalReports: number; projectedNextMonth: number; trend: string }>;
  }
  const [forecast, setForecast] = useState<HotspotForecast | null>(null);

  // Report-burst anomaly detection: a deterministic recent-rate vs.
  // baseline-rate comparison per category+ward, raised automatically by a
  // background sweep — same "no confident guess without real statistical
  // support" spirit as the hotspot forecast above.
  interface ReportAnomaly {
    id: string;
    category_id: string | null;
    category_name?: string;
    ward_number: number | null;
    recent_count: number;
    baseline_avg: number;
    window_minutes: number;
    reason: string;
    status: 'open' | 'acknowledged' | 'resolved';
    created_at: string;
  }
  const [anomalies, setAnomalies] = useState<ReportAnomaly[]>([]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
    loadDepartments();
    loadForecast();
    loadAnomalies();
  }, []);

  const loadForecast = async () => {
    try {
      const response = await apiService.get('/advanced-gis/hotspot-forecast');
      if (response.success) setForecast(response.data);
    } catch (err) {
      console.error('Error loading hotspot forecast:', err);
    }
  };

  const loadAnomalies = async () => {
    try {
      const response = await apiService.get('/advanced-gis/burst-anomalies?status=open');
      if (response.success) setAnomalies(response.data);
    } catch (err) {
      console.error('Error loading report anomalies:', err);
    }
  };

  const acknowledgeAnomaly = async (id: string) => {
    const res = await apiService.patch(`/advanced-gis/burst-anomalies/${id}`, { status: 'acknowledged' });
    if (res.success) loadAnomalies();
  };

  const resolveAnomaly = async (id: string) => {
    const notes = window.prompt('Resolution notes (optional):') || '';
    const res = await apiService.patch(`/advanced-gis/burst-anomalies/${id}`, { status: 'resolved', resolutionNotes: notes });
    if (res.success) loadAnomalies();
  };

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Load all data in parallel
      const [heatmap, clusters, areas, stats, techs] = await Promise.all([
        gisService.getHeatmapData(filters, departments),
        gisService.getClusteredPoints(filters),
        gisService.getServiceAreas(filters),
        gisService.getGISStats(filters),
        loadTechnicians(),
      ]);

      setHeatmapData(heatmap);
      setClusteredData(clusters);
      setServiceAreas(areas);
      setGisStats(stats);
      setTechnicians(techs);
    } catch (err) {
      console.error('GIS data failed to load:', err);
      setError('GIS data is currently unavailable');
      setHeatmapData([]);
      setClusteredData([]);
      setServiceAreas([]);
      setGisStats(null);
      setTechnicians([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await apiService.get('/departments');
      if (response.success) {
        setDepartments(response.data);
      }
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const loadTechnicians = async () => {
    try {
      const response = await apiService.get('/technician-locations');
      if (response.success) {
        return response.data;
      }
      return [];
    } catch (err) {
      console.error('Error loading technicians:', err);
      return [];
    }
  };

  // Mock data for Howick area when API fails
  const getMockData = () => {
    // Mock heatmap data - fault locations around Howick
    const mockHeatmapData: HeatmapPoint[] = [
      {
        id: '1',
        latitude: -29.4858, // Howick CBD
        longitude: 30.2306,
        intensity: 8,
        type: 'report',
        timestamp: new Date().toISOString(),
        metadata: { departmentId: 'dept1', categoryId: 'cat1', priority: 'high' }
      },
      {
        id: '2',
        latitude: -29.4872, // Howick Industrial Area
        longitude: 30.2289,
        intensity: 6,
        type: 'report',
        timestamp: new Date().toISOString(),
        metadata: { departmentId: 'dept2', categoryId: 'cat2', priority: 'medium' }
      },
      {
        id: '3',
        latitude: -29.4845, // Howick Residential Area
        longitude: 30.2321,
        intensity: 9,
        type: 'report',
        timestamp: new Date().toISOString(),
        metadata: { departmentId: 'dept3', categoryId: 'cat3', priority: 'critical' }
      },
      {
        id: '4',
        latitude: -29.4861, // Howick Shopping Centre
        longitude: 30.2298,
        intensity: 5,
        type: 'report',
        timestamp: new Date().toISOString(),
        metadata: { departmentId: 'dept4', categoryId: 'cat4', priority: 'low' }
      },
      {
        id: '5',
        latitude: -29.4839, // Howick Suburb
        longitude: 30.2315,
        intensity: 7,
        type: 'report',
        timestamp: new Date().toISOString(),
        metadata: { departmentId: 'dept1', categoryId: 'cat1', priority: 'high' }
      },
      {
        id: '6',
        latitude: -29.4880, // Howick West
        longitude: 30.2275,
        intensity: 4,
        type: 'report',
        timestamp: new Date().toISOString(),
        metadata: { departmentId: 'dept2', categoryId: 'cat2', priority: 'medium' }
      },
      {
        id: '7',
        latitude: -29.4820, // Howick East
        longitude: 30.2330,
        intensity: 6,
        type: 'report',
        timestamp: new Date().toISOString(),
        metadata: { departmentId: 'dept3', categoryId: 'cat3', priority: 'medium' }
      },
      {
        id: '8',
        latitude: -29.4890, // Howick North
        longitude: 30.2260,
        intensity: 3,
        type: 'report',
        timestamp: new Date().toISOString(),
        metadata: { departmentId: 'dept4', categoryId: 'cat4', priority: 'low' }
      }
    ];

    // Mock clustered data
    const mockClusteredData: ClusteredPoint[] = [
      {
        id: 'cluster1',
        latitude: -29.4858,
        longitude: 30.2306,
        pointCount: 3,
        pointIds: ['1', '2', '3'],
        departmentId: 'dept1',
        averageResponseTime: 2.5,
        averagePriority: 2,
        types: ['water', 'roads', 'electricity']
      },
      {
        id: 'cluster2',
        latitude: -29.4861,
        longitude: 30.2298,
        pointCount: 2,
        pointIds: ['4', '5'],
        departmentId: 'dept2',
        averageResponseTime: 1.8,
        averagePriority: 1.5,
        types: ['building', 'water']
      },
      {
        id: 'cluster3',
        latitude: -29.4880,
        longitude: 30.2275,
        pointCount: 2,
        pointIds: ['6', '8'],
        departmentId: 'dept3',
        averageResponseTime: 3.2,
        averagePriority: 1,
        types: ['roads', 'building']
      }
    ];

    // Mock service areas
    const mockServiceAreas: ServiceArea[] = [
      {
        id: 'area1',
        name: 'Howick CBD Service Area',
        departmentId: 'dept1',
        departmentName: 'Water & Sanitation',
        boundary: [
          { latitude: -29.4865, longitude: 30.2300 },
          { latitude: -29.4850, longitude: 30.2300 },
          { latitude: -29.4850, longitude: 30.2315 },
          { latitude: -29.4865, longitude: 30.2315 }
        ],
        areaKm2: 0.25,
        technicianCount: 3,
        averageResponseTime: 2.1,
        totalReports: 15,
        averagePriority: 2.3
      },
      {
        id: 'area2',
        name: 'Howick Industrial Zone',
        departmentId: 'dept2',
        departmentName: 'Roads & Infrastructure',
        boundary: [
          { latitude: -29.4880, longitude: 30.2280 },
          { latitude: -29.4865, longitude: 30.2280 },
          { latitude: -29.4865, longitude: 30.2295 },
          { latitude: -29.4880, longitude: 30.2295 }
        ],
        areaKm2: 0.35,
        technicianCount: 2,
        averageResponseTime: 2.8,
        totalReports: 8,
        averagePriority: 1.8
      }
    ];

    return { mockHeatmapData, mockClusteredData, mockServiceAreas };
  };

  const handleFilterChange = (key: keyof GISFilters, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Reload data with new filters
    setTimeout(() => {
      loadInitialData();
    }, 500);
  };

  const refreshData = () => {
    loadInitialData();
  };

  const renderHeatmapLayer = () => {
    if (!showHeatmap || heatmapData.length === 0) return null;

    return (
      <div className="absolute inset-0 pointer-events-none">
        {heatmapData.map((point) => (
          <div
            key={point.id}
            className="absolute w-4 h-4 rounded-full opacity-70"
            style={{
              left: `${((point.longitude + 180) / 360) * 100}%`,
              top: `${((90 - point.latitude) / 180) * 100}%`,
              backgroundColor: getHeatmapColor(point.intensity),
              transform: 'translate(-50%, -50%)',
              boxShadow: `0 0 20px ${getHeatmapColor(point.intensity)}`,
            }}
          />
        ))}
      </div>
    );
  };

  const renderClusterLayer = () => {
    if (!showClusters || clusteredData.length === 0) return null;

    return (
      <div className="absolute inset-0 pointer-events-none">
        {clusteredData.map((cluster) => (
          <div
            key={cluster.id}
            className="absolute rounded-full border-2 border-white shadow-lg cursor-pointer pointer-events-auto"
            style={{
              left: `${((cluster.longitude + 180) / 360) * 100}%`,
              top: `${((90 - cluster.latitude) / 180) * 100}%`,
              width: `${Math.max(20, Math.min(60, cluster.pointCount * 2))}px`,
              height: `${Math.max(20, Math.min(60, cluster.pointCount * 2))}px`,
              backgroundColor: getClusterColor(cluster.pointCount),
              transform: 'translate(-50%, -50%)',
            }}
            onClick={() => showClusterDetails(cluster)}
          >
            <div className="flex items-center justify-center h-full text-white text-xs font-bold">
              {cluster.pointCount}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderServiceAreaLayer = () => {
    if (!showServiceAreas || serviceAreas.length === 0) return null;

    return (
      <div className="absolute inset-0 pointer-events-none">
        {serviceAreas.map((area) => (
          <div
            key={area.id}
            className="absolute border-2 border-dashed cursor-pointer pointer-events-auto"
            style={{
              left: `${((area.boundary[0]?.longitude || 0) + 180) / 360 * 100}%`,
              top: `${((90 - (area.boundary[0]?.latitude || 0)) / 180) * 100}%`,
              width: `${Math.abs((area.boundary[2]?.longitude || 0) - (area.boundary[0]?.longitude || 0)) / 360 * 100}%`,
              height: `${Math.abs((area.boundary[2]?.latitude || 0) - (area.boundary[0]?.latitude || 0)) / 180 * 100}%`,
              borderColor: getDepartmentColor(area.departmentId),
              backgroundColor: `${getDepartmentColor(area.departmentId)}20`,
            }}
            onClick={() => showServiceAreaDetails(area)}
          />
        ))}
      </div>
    );
  };

  const getHeatmapColor = (intensity: number): string => {
    if (intensity > 70) return '#ef4444'; // red
    if (intensity > 40) return '#f97316'; // orange
    return '#eab308'; // yellow
  };

  const getClusterColor = (pointCount: number): string => {
    if (pointCount >= 100) return '#dc2626'; // red-600
    if (pointCount >= 50) return '#ea580c'; // orange-600
    if (pointCount >= 20) return '#d97706'; // amber-600
    if (pointCount >= 10) return '#ca8a04'; // yellow-600
    return '#16a34a'; // green-600
  };

  const getDepartmentColor = (departmentId: string): string => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    const index = parseInt(departmentId.replace(/\D/g, '')) % colors.length;
    return colors[index] || '#6b7280';
  };

  const showClusterDetails = (cluster: ClusteredPoint) => {
    alert(`Cluster Details:\nPoints: ${cluster.pointCount}\nLocation: ${cluster.latitude.toFixed(4)}, ${cluster.longitude.toFixed(4)}\nAvg Response Time: ${cluster.averageResponseTime?.toFixed(1)} min`);
  };

  const showServiceAreaDetails = (area: ServiceArea) => {
    alert(`Service Area: ${area.name}\nDepartment: ${area.departmentName}\nArea: ${area.areaKm2.toFixed(2)} km²\nTechnicians: ${area.technicianCount}\nAvg Response Time: ${area.averageResponseTime.toFixed(1)} min`);
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Points" value={gisStats?.totalPoints || 0} icon={MapPinIcon} tone="blue" />
        <StatCard label="Avg Response Time" value={`${gisStats?.averageResponseTime?.toFixed(1) || 0} min`} icon={ClockIcon} tone="emerald" />
        <StatCard label="Total Reports" value={gisStats?.totalReports || 0} icon={ExclamationTriangleIcon} tone="purple" />
        <StatCard label="Coverage" value={`${gisStats?.coveragePercentage?.toFixed(1) || 0}%`} icon={UserGroupIcon} tone="amber" />
      </div>

      {/* Department Breakdown */}
      {gisStats?.departmentBreakdown && (
        <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Department Performance</h3>
          <div className="space-y-3">
            {gisStats.departmentBreakdown.map((dept) => (
              <div key={dept.departmentId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{dept.departmentName}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{dept.pointCount} points</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900 dark:text-gray-100">{dept.averageResponseTime.toFixed(1)} min</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">avg response</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Map with all layers */}
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Interactive Map</h3>
        <div className="relative h-96 rounded-lg overflow-hidden">
          <GISMap
            heatmapData={heatmapData.map(point => ({
              lat: point.latitude,
              lng: point.longitude,
              weight: point.intensity || 1
            }))}
            clusteredData={clusteredData.map(cluster => ({
              lat: cluster.latitude,
              lng: cluster.longitude,
              count: cluster.pointCount || 1,
              reports: [] // We'll need to fetch actual reports if needed
            }))}
            serviceAreas={serviceAreas.map(area => ({
              id: area.id,
              name: area.name,
              coordinates: area.boundary?.map(coord => [coord.latitude, coord.longitude]) || [],
              color: '#3b82f6',
              opacity: 0.3
            }))}
            technicians={technicians.map(tech => ({
              id: tech.id || `tech_${tech.latitude}_${tech.longitude}`,
              userId: tech.user_id || tech.id,
              firstName: tech.first_name || 'Unknown',
              lastName: tech.last_name || 'Technician',
              role: tech.role || 'technician',
              departmentId: tech.department_id || '',
              departmentName: tech.department_name || 'Unknown',
              phone: tech.phone || '',
              latitude: tech.latitude,
              longitude: tech.longitude,
              lastUpdated: tech.last_updated || new Date().toISOString(),
              status: tech.status || 'offline',
              isActive: tech.is_active !== false,
            }))}
            showHeatmap={showHeatmap}
            showClusters={showClusters}
            showServiceAreas={showServiceAreas}
            showTechnicians={showTechnicians}
            onPointClick={(data) => console.log('Selected point:', data)}
          />
        </div>
      </div>
    </div>
  );

  const renderHeatmapTab = () => (
    <div className="space-y-6">
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Heatmap Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Radius (km)</label>
            <input
              type="range"
              min="5"
              max="100"
              value={filters.radiusKm || 50}
              onChange={(e) => handleFilterChange('radiusKm', parseInt(e.target.value))}
              className="w-full"
            />
            <p className="text-sm text-gray-600 dark:text-gray-400">{filters.radiusKm} km</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Department</label>
            <select
              value={filters.departmentId || ''}
              onChange={(e) => handleFilterChange('departmentId', e.target.value || undefined)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Type</label>
            <select
              value={filters.eventType || ''}
              onChange={(e) => handleFilterChange('eventType', e.target.value || undefined)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">All Types</option>
              <option value="report">Report</option>
              <option value="assignment">Assignment</option>
              <option value="escalation">Escalation</option>
              <option value="response_time">Response Time</option>
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Heatmap Data</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Intensity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Timestamp</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700">
              {heatmapData.slice(0, 10).map((point) => (
                <tr key={point.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {point.address || `${point.latitude.toFixed(4)}, ${point.longitude.toFixed(4)}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: getHeatmapColor(point.intensity) }}
                      />
                      <span className="text-sm text-gray-900 dark:text-gray-100">{point.intensity.toFixed(1)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {point.type.replace('_', ' ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(point.timestamp).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderClustersTab = () => (
    <div className="space-y-6">
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Cluster Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clusteredData.map((cluster) => (
            <div
              key={cluster.id}
              className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => showClusterDetails(cluster)}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: getClusterColor(cluster.pointCount) }}
                />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {cluster.pointCount} points
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {cluster.latitude.toFixed(4)}, {cluster.longitude.toFixed(4)}
              </p>
              {cluster.averageResponseTime && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Avg Response: {cluster.averageResponseTime.toFixed(1)} min
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderServiceAreasTab = () => (
    <div className="space-y-6">
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Service Areas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {serviceAreas.map((area) => (
            <div
              key={area.id}
              className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => showServiceAreaDetails(area)}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">{area.name}</h4>
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getDepartmentColor(area.departmentId) }}
                />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{area.departmentName}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Area:</span>
                  <span className="ml-1 font-medium">{area.areaKm2.toFixed(2)} km²</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Techs:</span>
                  <span className="ml-1 font-medium">{area.technicianCount}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Reports:</span>
                  <span className="ml-1 font-medium">{area.totalReports}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Response:</span>
                  <span className="ml-1 font-medium">{area.averageResponseTime.toFixed(1)} min</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Performance Analytics</h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Response Time Distribution</h4>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full"
                style={{ width: `${Math.min(100, (gisStats?.averageResponseTime || 0) / 60 * 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Average: {gisStats?.averageResponseTime?.toFixed(1) || 0} minutes
            </p>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Coverage Analysis</h4>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full"
                style={{ width: `${gisStats?.coveragePercentage || 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Service Coverage: {gisStats?.coveragePercentage?.toFixed(1) || 0}%
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Ward Hotspot Forecast</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          A statistical projection from real historical report counts, not an AI-generated estimate — treat as a planning signal, not a certainty.
        </p>
        {!forecast && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        )}
        {forecast && !forecast.available && (
          <p className="text-sm text-gray-600 dark:text-gray-400">{forecast.reason}</p>
        )}
        {forecast && forecast.available && (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Based on {forecast.monthsAnalyzed} months of report history.
            </p>
            {forecast.wards && forecast.wards.length > 0 ? (
              <div className="space-y-2">
                {forecast.wards.map((w) => (
                  <div key={w.wardNumber} className="flex items-center justify-between gap-2 rounded-sm border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                    <div className="text-sm">
                      <span className="font-medium text-gray-900 dark:text-gray-100">Ward {w.wardNumber}</span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        {w.totalReports} reports over {w.monthsOfData} months
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`capitalize ${w.trend === 'rising' ? 'text-red-600' : w.trend === 'falling' ? 'text-green-600' : 'text-gray-500'}`}>
                        {w.trend}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        ~{w.projectedNextMonth} projected next month
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No wards have enough historical data yet for a reliable projection.</p>
            )}
          </>
        )}
      </div>

      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Report Burst Anomalies</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          A statistical comparison against this category+ward's own recent history, not an AI-generated guess — flagged only when the recent rate is a real multiple of the baseline.
        </p>
        {anomalies.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No unusual report bursts detected right now.</p>
        ) : (
          <div className="space-y-2">
            {anomalies.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 rounded-sm border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-900/10">
                <div className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {a.category_name || 'Uncategorized'}{a.ward_number != null ? ` — Ward ${a.ward_number}` : ''}
                  </span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{a.reason}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="secondary" size="sm" onClick={() => acknowledgeAnomaly(a.id)}>Acknowledge</Button>
                  <Button variant="primary" size="sm" onClick={() => resolveAnomaly(a.id)}>Resolve</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading GIS data</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="GIS Analytics"
        subtitle="Spatial analysis and visualization of service operations"
        actions={
          <Button variant="secondary" icon={ArrowPathIcon} onClick={refreshData}>
            Refresh
          </Button>
        }
      />

      {/* Layer Controls */}
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Layer Visibility</h3>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showHeatmap}
              onChange={(e) => setShowHeatmap(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Heatmap</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showClusters}
              onChange={(e) => setShowClusters(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Clusters</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showServiceAreas}
              onChange={(e) => setShowServiceAreas(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Service Areas</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showTechnicians}
              onChange={(e) => setShowTechnicians(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Technicians</span>
          </label>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: ChartBarIcon },
              { id: 'heatmap', label: 'Heatmap', icon: MapPinIcon },
              { id: 'clusters', label: 'Clusters', icon: UserGroupIcon },
              { id: 'areas', label: 'Service Areas', icon: InformationCircleIcon },
              { id: 'analytics', label: 'Analytics', icon: ChartBarIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 inline mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'heatmap' && renderHeatmapTab()}
          {activeTab === 'clusters' && renderClustersTab()}
          {activeTab === 'areas' && renderServiceAreasTab()}
          {activeTab === 'analytics' && renderAnalyticsTab()}
        </div>
      </div>
    </div>
  );
};

export default GISAnalytics;
